"use client";

import { useMemo, useState } from "react";
import { Check, Copy } from "lucide-react";
import { joaatAll } from "@/lib/freeTools/joaat";
import { ShowTourButton } from "@/components/freeTools/ShowTourButton";
import { ToolWalkthrough } from "@/components/walkthrough/ToolWalkthrough";
import {
  finishLabel as joaatFinishLabel,
  steps as joaatSteps,
} from "@/components/walkthrough/steps/joaat";

const EXAMPLES = ["weapon_pistol", "police3", "adder", "s_m_y_cop_01", "mp_m_freemode_01"];

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      aria-label="Copy to clipboard"
      onClick={() => {
        try {
          void navigator.clipboard?.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        } catch {
          /* clipboard unavailable — no-op */
        }
      }}
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border/60 text-muted-foreground transition-colors hover:border-primary/30 hover:text-foreground"
    >
      {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
    </button>
  );
}

function ResultRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border/60 bg-card px-4 py-3">
      <span className="w-24 shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <code className="min-w-0 flex-1 truncate font-mono text-sm text-foreground">{value}</code>
      <CopyButton value={value} />
    </div>
  );
}

export function JoaatClient() {
  const [input, setInput] = useState("weapon_pistol");
  const result = useMemo(() => joaatAll(input), [input]);
  const hasInput = input.length > 0;
  // Manual mode only: nothing here ever auto-starts over a name being hashed.
  const [tourSignal, setTourSignal] = useState(0);

  return (
    <div className="not-prose rounded-2xl border border-border/60 bg-muted/30 p-5 sm:p-6">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <label htmlFor="joaat-input" className="block text-sm font-medium text-foreground">
          String to hash
        </label>
        <ShowTourButton
          testId="joaat-show-tour"
          onClick={() => setTourSignal((signal) => signal + 1)}
        />
      </div>
      <input
        id="joaat-input"
        data-walkthrough="jh-input"
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        spellCheck={false}
        autoComplete="off"
        autoCapitalize="off"
        placeholder="e.g. weapon_pistol"
        className="w-full rounded-xl border border-border bg-background px-4 py-3 font-mono text-sm text-foreground outline-none transition-colors focus:border-primary/50"
      />

      <div
        data-walkthrough="jh-examples"
        className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground"
      >
        <span className="mr-1">Try:</span>
        {EXAMPLES.map((ex) => (
          <button
            key={ex}
            type="button"
            onClick={() => setInput(ex)}
            className="rounded-md border border-border/60 px-2 py-0.5 font-mono transition-colors hover:border-primary/30 hover:text-foreground"
          >
            {ex}
          </button>
        ))}
      </div>

      {hasInput ? (
        <div data-walkthrough="jh-results" className="mt-5 space-y-2.5">
          <p className="text-xs text-muted-foreground">
            Hashing <code className="font-mono text-foreground">{result.normalized}</code> (lowercased)
          </p>
          <ResultRow label="Unsigned" value={String(result.unsigned)} />
          <ResultRow label="Signed" value={String(result.signed)} />
          <ResultRow label="Hex" value={result.hex} />
        </div>
      ) : (
        <p className="mt-5 text-sm text-muted-foreground">Type a name to see its JOAAT hash.</p>
      )}

      <ToolWalkthrough
        steps={joaatSteps}
        storageKey="workshop_tour_joaat_v1"
        finishLabel={joaatFinishLabel}
        openSignal={tourSignal}
      />
    </div>
  );
}
